/**
 * External dependencies
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Stack, Heading, Divider } from '@towersystems/towersystems-design-system';
import page from 'page';
import { get } from 'lodash';

/**
 * Internal dependencies
 */
import Main from 'components/main';
import {
	requestConnection,
	requestConnectionSalesMeta,
	exportNewConnectionSales,
	saveConnection,
} from 'state/connections/actions';
import Navigation from 'modules/dashboard/connections/navigation';
import Button from 'components/button';
import ButtonGroup from 'components/button-group';
import { errorNotice, successNotice } from 'state/notices/actions';
import LoadingPlaceholder from 'modules/dashboard/connections/loading-placeholder';
import { requestLocations } from 'state/locations/actions';
import { getAllLocations } from 'state/locations/selectors';
import { requestTerminals } from 'state/terminals/actions';
import { requestPaytypes } from 'state/paytypes/actions';
import DefaultRender from './default-render';
import RoamRender from './roam-render';
import BigCommerceRender from './big-commerce-render';
import ShopifyGraphQLRender from './shopify-graphql-render';
import WooCommerceRender from './woo-commerce-render';

const SalesMain = ({ connectionId, ...props }) => {
	const [requesting, setRequesting] = useState(true);
	const [connection, setConnection] = useState(null);
	const [metaData, setMetaData] = useState(null);
	const [terminals, setTerminals] = useState(null);
	const [paytypes, setPaytypes] = useState(null);
	const [values, setValues] = useState({});
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		loadData(connectionId);
	}, []);

	const loadData = async (connectionId) => {
		setRequesting(true);

		try {
			let connection = await props.requestConnection(connectionId);
			await props.requestLocations();
			setConnection(connection);
			setValues(connection.config);
			let metaData = await props.requestConnectionSalesMeta(connectionId);
			setMetaData(metaData);
			let terminals = await props.requestTerminals();
			setTerminals(terminals);
			let paytypes = await props.requestPaytypes();
			setPaytypes(paytypes);
		} catch (error) {
			console.log('error', error);
			page('/');
		}

		setRequesting(false);
	};

	const handleOnGeneralSettingsChange = (newValues) => {
		setValues({ ...values, ...newValues });
	};

	// need switch statement that changes depending on connection type
	const handleOnChangeCheckbox = async (e) => {
		let n = e.target.name;
		let v = !get(values, n);
		let b = { ...values };
		let updated = { ...values, [n]: v };
		setValues(updated);
	};

	const handleOnSave = async () => {
		try {
			setSaving(true);
			await props.saveConnection(connectionId, { config: values });
			props.successNotice('Saved');
		} catch (error) {
			props.errorNotice('Save failed');
		}
		setSaving(false);
	};

	const connectionGroupCode = get(connection, 'group_code');

	const render = (connectionType) => {
		switch (connectionType) {
			case 'roam':
				return (
					<RoamRender
						exportNewSales={exportNewSales}
						handleOnGeneralSettingsChange={handleOnGeneralSettingsChange}
						handleOnChangeCheckbox={handleOnChangeCheckbox}
						handleOnChangeInputBox={handleOnChangeInputBox}
						connectionGroupCode={connectionGroupCode}
						terminals={terminals}
						paytypes={paytypes}
						values={values}
						metaData={metaData}
						connectionId={connectionId}
						connectionType={connectionType}
						{...props}
					/>
				);
			case 'shopify_graphql':
				return (
					<ShopifyGraphQLRender
						exportNewSales={exportNewSales}
						handleOnGeneralSettingsChange={handleOnGeneralSettingsChange}
						handleOnChangeCheckbox={handleOnChangeCheckbox}
						handleOnChangeInputBox={handleOnChangeInputBox}
						connectionGroupCode={connectionGroupCode}
						terminals={terminals}
						paytypes={paytypes}
						values={values}
						metaData={metaData}
						connectionId={connectionId}
						connectionType={connectionType}
						{...props}
					/>
				);
			case 'big_commerce':
				return (
					<BigCommerceRender
						exportNewSales={exportNewSales}
						handleOnGeneralSettingsChange={handleOnGeneralSettingsChange}
						handleOnChangeCheckbox={handleOnChangeCheckbox}
						handleOnChangeInputBox={handleOnChangeInputBox}
						connectionGroupCode={connectionGroupCode}
						terminals={terminals}
						paytypes={paytypes}
						values={values}
						metaData={metaData}
						connectionId={connectionId}
						connectionType={connectionType}
						{...props}
					/>
				);
			case 'woo_commerce':
				return (
					<WooCommerceRender
						exportNewSales={exportNewSales}
						handleOnGeneralSettingsChange={handleOnGeneralSettingsChange}
						handleOnChangeCheckbox={handleOnChangeCheckbox}
						handleOnChangeInputBox={handleOnChangeInputBox}
						connectionGroupCode={connectionGroupCode}
						terminals={terminals}
						paytypes={paytypes}
						values={values}
						metaData={metaData}
						connectionId={connectionId}
						connectionType={connectionType}
						{...props}
					/>
				);
			default:
				return (
					<DefaultRender
						exportNewSales={exportNewSales}
						handleOnGeneralSettingsChange={handleOnGeneralSettingsChange}
						handleOnChangeCheckbox={handleOnChangeCheckbox}
						handleOnChangeInputBox={handleOnChangeInputBox}
						connectionGroupCode={connectionGroupCode}
						terminals={terminals}
						paytypes={paytypes}
						values={values}
						metaData={metaData}
						connectionId={connectionId}
						connectionType={connectionType}
						{...props}
					/>
				);
		}
	};

	const exportNewSales = async () => {
		try {
			let metaData = await props.exportNewConnectionSales(connectionId);
			setMetaData(metaData);
			props.successNotice('Search complete');
		} catch (error) {
			props.errorNotice('Failed to search for sales');
		}
	};

	const handleOnChangeInputBox = async (e) => {
		let n = e.target.name;
		let v = e.target.value;

		let b = { ...values };
		let updated = { ...values, [n]: v };
		setValues(updated);

		try {
			await props.saveConnection(connectionId, { config: { [n]: v } });
			// props.successNotice('Saved');
		} catch (error) {
			setValues(b);
		}
	};

	if (true === requesting) {
		return <LoadingPlaceholder connectionId={connectionId} section='sales' />;
	}

	const connectionType = get(connection, ['connection_type', 'code']);

	return (
		<Main>
			<Navigation connectionId={connectionId} section='sales' />

			<Box paddingTop='xxlarge' paddingBottom='xxlarge'>
				<Box display='flex' justifyContent='spaceBetween' alignItems='center' paddingBottom='xlarge'>
					<Heading level='1'>Sales</Heading>
					<ButtonGroup>
						<Button disabled={saving} onClick={handleOnSave}>
							{saving ? 'Saving...' : 'Save'}
						</Button>
					</ButtonGroup>
				</Box>
				<Divider />
			</Box>
			<Stack space='medium'>{render(connectionType)}</Stack>
		</Main>
	);
};

export default connect(
	(state) => ({
		locations: getAllLocations(state),
	}),
	{
		requestConnection,
		errorNotice,
		successNotice,
		saveConnection,
		requestConnectionSalesMeta,
		exportNewConnectionSales,
		requestLocations,
		requestTerminals,
		requestPaytypes,
	},
)(SalesMain);
